/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { protectedResources } from "./authConfig";
import { msalInstance } from "./index";


const getToken = async () => {
    const account = msalInstance.getActiveAccount();

    if (!account) {
        throw Error("No active account! Verify a user has been signed in and setActiveAccount has been called.");
    }

    const response = await msalInstance.acquireTokenSilent({
        account: account,
        ...protectedResources.apiTodoList.scopes
    });

    return response.accessToken;
}

// Utility function to handle common fetch errors
const handleFetchError = (error, functionName) => {
    console.log(`Error in ${functionName}:`, error);
    
    // Check for specific error types
    if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        return {
            data: null,
            status: 0,
            error: 'Network error: Unable to connect to server. Please check your internet connection.'
        };
    }
    
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
        return {
            data: null,
            status: 0,
            error: 'Network error: Request failed. Please try again.'
        };
    }
    
    return {
        data: null,
        status: 500,
        error: error.message || 'An unexpected error occurred'
    };
}

// Retry mechanism for failed requests
const fetchWithRetry = async (url, options, maxRetries = 3, delay = 1000) => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
            // Add timeout to prevent hanging requests
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
            
            const response = await fetch(url, {
                ...options,
                signal: controller.signal
            });
            
            clearTimeout(timeoutId);
            
            if (!response.ok) {
                // Don't retry on client errors (4xx)
                if (response.status >= 400 && response.status < 500) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                // Retry on server errors (5xx) or network issues
                if (attempt === maxRetries) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                console.log(`Attempt ${attempt} failed with status ${response.status}, retrying...`);
                await new Promise(resolve => setTimeout(resolve, delay * attempt));
                continue;
            }
            
            return response;
        } catch (error) {
            if (error.name === 'AbortError') {
                console.log(`Attempt ${attempt} timed out after 30 seconds`);
            }
            
            if (attempt === maxRetries) {
                throw error;
            }
            console.log(`Attempt ${attempt} failed:`, error.message, 'Retrying...');
            await new Promise(resolve => setTimeout(resolve, delay * attempt));
        }
    }
}

export const getTasks = async () => {
    try {
        console.log("getTasks: Starting API call...");
        const accessToken = await getToken();
        console.log("getTasks: Token acquired successfully");

        const headers = new Headers();
        const bearer = `Bearer ${accessToken}`;

        headers.append("Authorization", bearer);
        headers.append("Content-Type", "application/json");

        const options = {
            method: "GET",
            headers: headers
        };

        console.log("getTasks: Making request to:", protectedResources.apiTodoList.todoListEndpoint);
        const response = await fetchWithRetry(protectedResources.apiTodoList.todoListEndpoint, options);
        console.log("getTasks: Response received, status:", response.status);
        
        const data = await response.json();
        console.log("getTasks: Data parsed successfully");
        
        return {
            data: data,
            status: response.status
        };
    } catch (error) {
        console.error("getTasks: Error occurred:", error);
        return handleFetchError(error, "getTasks");
    }
}

export const getSubscriptionDetails = async (id) => {
    try {
        const accessToken = await getToken();

        const headers = new Headers();
        const bearer = `Bearer ${accessToken}`;

        headers.append("Authorization", bearer);
        headers.append("Content-Type", "application/json");

        const options = {
            method: "GET",
            headers: headers
        };

        const endpoint = window.REACT_APP_APIENDPOINT+"/api/v2/support/"+id+"/subscription"

        const response = await fetch(endpoint, options);
        
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        
        return {
            data: data,
            status: response.status
        };
    } catch (error) {
        console.log("Error in getSubscriptionDetails:", error);
        return {
            data: null,
            status: 500,
            error: error.message
        };
    }
}

export const getSignupDetailsForCust = async (id) => {
    try {
        const accessToken = await getToken();

        const headers = new Headers();
        const bearer = `Bearer ${accessToken}`;

        headers.append("Authorization", bearer);

        const options = {
            method: "GET",
            headers: headers
        };

        const response = await fetch(protectedResources.apiTodoList.getSignupEndpoint+`/${id}`, options);
        const data = await response.json();
        
        return {
            data: data,
            status: response.status
        };
    } catch (error) {
        console.log("Error in getSignupDetailsForCust:", error);
        return {
            data: null,
            status: 500,
            error: error.message
        };
    }
}

export const getSignupDetails = async (id,cs_role) => {
    try {
        const accessToken = await getToken();

        const headers = new Headers();
        const bearer = `Bearer ${accessToken}`;

        headers.append("Authorization", bearer);
        //headers.append('Content-Type', 'application/json');
        //headers.append("Access-Control-Allow-Origin", "*");
        //headers.append("Access-Control-Allow-Methods", "GET, POST, PATCH, PUT, DELETE, OPTIONS");
        //headers.append("Access-Control-Allow-Headers", "Origin, Content-Type, X-Auth-Token");

        const options = {
            method: "GET",
            headers: headers
        };

        const endpoint = cs_role 
        ? protectedResources.apiTodoList.getSignupEndpoint 
        : protectedResources.apiTodoList.todoListEndpoint;

        const response = await fetch(`${endpoint}/${id}`, options);
        const data = await response.json();
        
        return {
            data: data,
            status: response.status
        };
    } catch (error) {
        console.log("Error in getSignupDetails:", error);
        return {
            data: null,
            status: 500,
            error: error.message
        };
    }
}

export const getPackages = async (id) => {
    try {
        const accessToken = await getToken();
        const headers = new Headers();
        const bearer = `Bearer ${accessToken}`;
        headers.append("Authorization", bearer);
        const options = {
            method: "GET",
            headers: headers
        };
        const getPackages = window.REACT_APP_APIENDPOINT+"/api/v2/support/"+id+"/packages";
        
        const response = await fetch(getPackages, options);
        const data = await response.json();
        
        return {
            data: data,
            status: response.status
        };
    } catch (error) {
        console.log("Error in getPackages:", error);
        return {
            data: null,
            status: 500,
            error: error.message
        };
    }
}

export const getInvoicePackage = async (id) => {
    try {
        const accessToken = await getToken();

        const headers = new Headers();
        const bearer = `Bearer ${accessToken}`;

        headers.append("Authorization", bearer);

        const options = {
            method: "GET",
            headers: headers
        };

        const getPackages = window.REACT_APP_APIENDPOINT+"/api/v2/support/"+id+"/subscription/packages";

        const response = await fetch(getPackages, options);
        const data = await response.json();
        
        return {
            data: data,
            status: response.status
        };
    } catch (error) {
        console.log("Error in getInvoicePackage:", error);
        return {
            data: null,
            status: 500,
            error: error.message
        };
    }
}

export const getCSPeople = async (id) => {
    try {
        const accessToken = await getToken();

        const headers = new Headers();
        const bearer = `Bearer ${accessToken}`;

        headers.append("Authorization", bearer);
        headers.append("Content-Type", "application/json");

        const options = {
            method: "GET",
            headers: headers
        };

        const getCS = window.REACT_APP_APIENDPOINT+"/api/v2/support/employee/CS";

        const response = await fetchWithRetry(getCS, options);
        const data = await response.json();
        
        return {
            data: data,
            status: response.status
        };
    } catch (error) {
        return handleFetchError(error, "getCSPeople");
    }
}

export const getSalesPeople = async (id) => {
    try {
        const accessToken = await getToken();

        const headers = new Headers();
        const bearer = `Bearer ${accessToken}`;

        headers.append("Authorization", bearer);

        const options = {
            method: "GET",
            headers: headers
        };

        const getSales = window.REACT_APP_APIENDPOINT+"/api/v2/support/employee/sales";

        const response = await fetch(getSales, options);
        const data = await response.json();
        
        return {
            data: data,
            status: response.status
        };
    } catch (error) {
        console.log("Error in getSalesPeople:", error);
        return {
            data: null,
            status: 500,
            error: error.message
        };
    }
}

export const getServiceProviders = async (id) => {
    try {
        const accessToken = await getToken();

        const headers = new Headers();
        const bearer = `Bearer ${accessToken}`;

        headers.append("Authorization", bearer);

        const options = {
            method: "GET",
            headers: headers
        };

        const getPackages = window.REACT_APP_APIENDPOINT+"/api/v2/support/serviceprovidertype";

        const response = await fetch(getPackages, options);
        const data = await response.json();
        
        return {
            data: data,
            status: response.status
        };
    } catch (error) {
        console.log("Error in getServiceProviders:", error);
        return {
            data: null,
            status: 500,
            error: error.message
        };
    }
}

export const getTimeZones = async () => {
    try {
        const accessToken = await getToken();

        const headers = new Headers();
        const bearer = `Bearer ${accessToken}`;

        headers.append("Authorization", bearer);

        const options = {
            method: "GET",
            headers: headers
        };

        const getTimeZones = window.REACT_APP_APIENDPOINT+"/api/v2/auth/time_zone";

        const response = await fetch(getTimeZones, options);
        const data = await response.json();
        
        return {
            data: data,
            status: response.status
        };
    } catch (error) {
        console.log("Error in getTimeZones:", error);
        return {
            data: null,
            status: 500,
            error: error.message
        };
    }
}

export const getAdmins = async (id) => {
    try {
        const accessToken = await getToken();

        const headers = new Headers();
        const bearer = `Bearer ${accessToken}`;

        headers.append("Authorization", bearer);
        headers.append("Content-Type", "application/json");

        const options = {
            method: "GET",
            headers: headers
        };

        const getAdmins = window.REACT_APP_APIENDPOINT+"/api/v2/support/"+id+"/admins";

        const response = await fetchWithRetry(getAdmins, options);
        const data = await response.json();
        
        return {
            data: data,
            status: response.status
        };
    } catch (error) {
        return handleFetchError(error, "getAdmins");
    }
}

export const getAIOpsFeatures = async (id) => {
    try {
        const accessToken = await getToken();

        const headers = new Headers();
        const bearer = `Bearer ${accessToken}`;

        headers.append("Authorization", bearer);

        const options = {
            method: "GET",
            headers: headers
        };

        const getAIOpsFeatures = window.REACT_APP_APIENDPOINT+"/api/v2/support/"+id+"/addon";

        const response = await fetch(getAIOpsFeatures, options);
        const data = await response.json();
        
        return {
            data: data,
            status: response.status
        };
    } catch (error) {
        console.log("Error in getAIOpsFeatures:", error);
        return {
            data: null,
            status: 500,
            error: error.message
        };
    }
}

export const toggleAIOpsFeature = async (id, featureNumber, enable) => {
    try {
        const accessToken = await getToken();
        const headers = new Headers();
        const bearer = `Bearer ${accessToken}`;
        headers.append("Authorization", bearer);
        headers.append('Content-Type', 'application/json');
        
        const url = window.REACT_APP_APIENDPOINT+"/api/v2/support/"+id+"/addon/"+featureNumber+"/config";
        
        const options = {
            method: enable ? "POST" : "DELETE",
            headers: headers
        };
        
        if (enable) {
            options.body = JSON.stringify({"config_data":"{}"});
        }
        
        const response = await fetch(url, options);
        const data = await response.json();
        
        return {
            data: data,
            status: response.status
        };
    } catch (error) {
        console.log(error);
        return { data: null, status: 0, error: error.message };
    }
}

export const getCustomerDetails = async () => {
    try {
        const accessToken = await getToken();

        const headers = new Headers();
        const bearer = `Bearer ${accessToken}`;

        headers.append("Authorization", bearer);
        headers.append("Content-Type", "application/json");

        const options = {
            method: "GET",
            headers: headers
        };

        const response = await fetch(protectedResources.apiTodoList.getSignupEndpoint, options);
        
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        
        return {
            data: data,
            status: response.status
        };
    } catch (error) {
        return handleFetchError(error, "getCustomerDetails");
    }
}

export const addInvoice = async (accountUrl, annualFee, userCount, stakeholderFee, stakeholderCount, callRoutingFee, subscriptionStartDate, invoicePlan, customerSuccess, serviceProvider, customer_segment, salesPersonId,comments) => {

    try {
        const accessToken = await getToken();

        const headers = new Headers();
        const bearer = `Bearer ${accessToken}`;

        headers.append("Authorization", bearer);
        headers.append('Content-Type', 'application/json');

        const options = {
            method: "POST",
            headers: headers,
            body: JSON.stringify({
                "annual_fee": `${annualFee}`,
                "user_licenses": `${userCount}`,
                "user_fee": `${annualFee}`,
                "stakeholder_fee": `${stakeholderFee}`,
                "stakeholder_licenses": `${stakeholderCount}`,
                "call_routing_fee": `${callRoutingFee}`,
                "subscription_start_date": `${subscriptionStartDate}`,
                "product_package_id": `${invoicePlan}`,
                "customer_success_id": `${customerSuccess}`,
                "service_provider_type": `${serviceProvider}`,
                "customer_segment": `${customer_segment}`,
                "sales_person_id": `${salesPersonId}`,
                "comments": `${comments}`
            })
        };

        const enableInvoice = window.REACT_APP_APIENDPOINT+"/api/v2/support/"+accountUrl+"/subscription";

        const response = await fetch(enableInvoice, options);
        const data = await response.json();
        
        return {
            data: data,
            status: response.status
        };

    } catch (error) {
        console.log("Error in addInvoice:", error);
        return {
            data: null,
            status: 500,
            error: error.message
        };
    }

}



export const renewInvoice = async (accountUrl,userCount, annualFee, stakeholderCount, stakeholderFee,callRoutingFee, subscriptionPackageId, effectivedate, reason) => {
    try {
        console.log(accountUrl,userCount, annualFee, stakeholderCount, stakeholderFee,callRoutingFee, subscriptionPackageId, effectivedate, reason);
        const accessToken = await getToken();

        const headers = new Headers();
        const bearer = `Bearer ${accessToken}`;

        headers.append("Authorization", bearer);
        headers.append('Content-Type', 'application/json');
        const options = {
            method: "PUT",
            headers: headers,
            body: JSON.stringify({
                "annual_fee": `${annualFee}`,
                "user_licenses": `${userCount}`,
                "user_fee":`${annualFee}`,
                "stakeholder_fee": `${stakeholderFee}`,
                "stakeholder_licenses": `${stakeholderCount}`,
                "call_routing_fee": `${callRoutingFee}`,
                "effective_date": `${effectivedate}`,
                "product_package_id":`${subscriptionPackageId}`,
                "change_type": `renewal`,
                "comments": `${reason}`
            })
        };
        const renew_invoice = window.REACT_APP_APIENDPOINT+"/api/v2/support/"+accountUrl+"/subscription";

        const response = await fetch(renew_invoice, options);
        const data = await response.json();
        
        return {
            data: data,
            status: response.status
        };

    } catch (error) {
        console.log("Error in renewInvoice:", error);
        return {
            data: null,
            status: 500,
            error: error.message
        };
    }

}

export const addUsers = async (accountUrl,userCount, annualFee, stakeholderCount, stakeholderFee,callRoutingFee,subscriptionPackageId,effectivedate,reason) => {

    try {
        const accessToken = await getToken();

        const headers = new Headers();
        const bearer = `Bearer ${accessToken}`;

        headers.append("Authorization", bearer);
        headers.append('Content-Type', 'application/json');
        const options = {
            method: "PUT",
            headers: headers,
            body: JSON.stringify({
                "annual_fee": `${annualFee}`,
                "user_licenses": `${userCount}`,
                "user_fee":`${annualFee}`,
                "stakeholder_fee": `${stakeholderFee}`,
                "stakeholder_licenses": `${stakeholderCount}`,
                "call_routing_fee": `${callRoutingFee}`,
                "product_package_id":`${subscriptionPackageId}`,
                "effective_date": `${effectivedate}`,
                "change_type": `Addlicenses`,
                "comments": `${reason}`
            })
        };
        const addUsersEndpoint = window.REACT_APP_APIENDPOINT+"/api/v2/support/"+accountUrl+"/subscription";

        const response = await fetch(addUsersEndpoint, options);
        const data = await response.json();
        
        return {
            data: data,
            status: response.status
        };

    } catch (error) {
        console.log("Error in addUsers:", error);
        return {
            data: null,
            status: 500,
            error: error.message
        };
    }

}

export const cancelInvoice = async (accountUrl, effectivedate, comments) => {

    try {
        const accessToken = await getToken();

        const headers = new Headers();
        const bearer = `Bearer ${accessToken}`;

        headers.append("Authorization", bearer);
        headers.append('Content-Type', 'application/json');
        const options = {
            method: "PUT",
            headers: headers,
            body: JSON.stringify({
                "effective_date": `${effectivedate}`,
                "change_type": `cancel`,
                "comments": `${comments}`
            })
        };
        const cancel_invoice = window.REACT_APP_APIENDPOINT+"/api/v2/support/"+accountUrl+"/subscription";

        const response = await fetch(cancel_invoice, options);
        const data = await response.json();
        
        return {
            data: data,
            status: response.status
        };

    } catch (error) {
        console.log("Error in cancelInvoice:", error);
        return {
            data: null,
            status: 500,
            error: error.message
        };
    }

}



export const assignCs = async (accountUrl, csPersonId, service_provider_type = '', customer_segment = '') => {

    try {
        const accessToken = await getToken();

        const headers = new Headers();
        const bearer = `Bearer ${accessToken}`;

        headers.append("Authorization", bearer);
        headers.append('Content-Type', 'application/json');
        const options = {
            method: "PUT",
            headers: headers,
            body: JSON.stringify({
                "customer_success_id": `${csPersonId}`,
                "change_type": `CSChange`,
                "service_provider_type": `${service_provider_type}`,
                "customer_segment": `${customer_segment}`
            })
        };
        const assigncs = window.REACT_APP_APIENDPOINT+"/api/v2/support/"+accountUrl+"/subscription";

        const response = await fetch(assigncs, options);
        const data = await response.json();
        
        return {
            data: data,
            status: response.status
        };

    } catch (error) {
        console.log("Error in assignCs:", error);
        return {
            data: null,
            status: 500,
            error: error.message
        };
    }

}

export const enableLCR = async (id,twilioId,country,state,areacode) => {

    try {
        const accessToken = await getToken();

        const headers = new Headers();
        const bearer = `Bearer ${accessToken}`;

        headers.append("Authorization", bearer);
        headers.append('Content-Type', 'application/json');

        const options = {
            method: "POST",
            headers: headers,
            body: JSON.stringify({
                "account": `${id}`,
                "sub_account": `${twilioId}`,
                "country": `${country}`,
                "state": `${state}`,
                "area_code": `${areacode}`
              })
        };

        const response = await fetch(protectedResources.apiTodoList.addLCREndpoint, options);
        const status = await response.status;
        
        return {
            data: null,
            status: status,
            error: null
        };

    } catch (error) {
        console.log("Error in enableLCR:", error);
        return {
            data: null,
            status: 500,
            error: error.message
        };
    }

}

//Change Package
export const changePackage = async (id,packageChange, package_id) => {
    try {
        const accessToken = await getToken();
        const headers = new Headers();
        const bearer = `Bearer ${accessToken}`;
        headers.append("Authorization", bearer);
        headers.append('Content-Type', 'application/json');
        const options = {
            method: "PUT",
            headers: headers,
            body: JSON.stringify({
                "id": `${package_id}`
              })
        };
        const changePlan = window.REACT_APP_APIENDPOINT+"/api/v2/support/"+id+"/packages";
        const response = await fetch(changePlan, options);
        const data = await response.json();
        
        return {
            data: data,
            status: response.status
        };
    } catch (error) {
        console.log("Error in changePackage:", error);
        return {
            data: null,
            status: 500,
            error: error.message
        };
    }
}

//Change Time zone
export const changeTimeZone = async (id,timezone_id) => {

    try {
        const accessToken = await getToken();

        const headers = new Headers();
        const bearer = `Bearer ${accessToken}`;

        headers.append("Authorization", bearer);
        headers.append('Content-Type', 'application/json');

        const options = {
            method: "PUT",
            headers: headers,
            body: JSON.stringify({
                "new_timezone_id": `${timezone_id}`
              })
        };

        const changeTimeZone = window.REACT_APP_APIENDPOINT+"/api/v2/support/"+id+"/time_zone";

        const response = await fetch(changeTimeZone, options);
        const data = await response.json();
        
        return {
            data: data,
            status: response.status
        };

    } catch (error) {
        console.log("Error in changeTimeZone:", error);
        return {
            data: null,
            status: 500,
            error: error.message
        };
    }

}


//Change Owner
export const changeOwner = async (id,ownerUsername) => {
    try {
        const accessToken = await getToken();
        const headers = new Headers();
        const bearer = `Bearer ${accessToken}`;
        headers.append("Authorization", bearer);
        headers.append('Content-Type', 'application/json');
        const options = {
            method: "PUT",
            headers: headers,
            body: JSON.stringify({
                "username": `${ownerUsername}`
              })
        };
        const changePlan = window.REACT_APP_APIENDPOINT+"/api/v2/support/"+id+"/owner";
        const response = await fetch(changePlan, options);
        const status = await response.status;
        
        return {
            data: null,
            status: status,
            error: null
        };
    } catch (error) {
        console.log("Error in changeOwner:", error);
        return {
            data: null,
            status: 500,
            error: error.message
        };
    }
}


export const addPipeDeal = async (id) => {
    try {
        const accessToken = await getToken();

        const headers = new Headers();
        const bearer = `Bearer ${accessToken}`;

        headers.append('Content-Type', 'application/json');
        headers.append('Accept', 'application/json');

        const options = {
            method: "POST",
            headers: headers,
            body: JSON.stringify({
                "title": `${id}`
              })
        };

        const response = await fetch(protectedResources.apiTodoList.pipeAddDealEndpoint, options);
        const data = await response.json();
        
        return {
            data: data,
            status: response.status
        };
    } catch (error) {
        console.log("Error in addPipeDeal:", error);
        return {
            data: null,
            status: 500,
            error: error.message
        };
    }
}

// GET DEAL DETAILS IN PIPEDRIVE FROM DEAL ID
export const getDealDetailsFromPipeDealID = async (id) => {

    try {
        const headers = new Headers();
        headers.append("Content-Type", "application/json");

        const options = {
            method: "GET",
            headers: headers
        };
        const getDealDets = window.REACT_APP_PIPEDRIVEAPI+"/deals/"+id+"?api_token="+window.REACT_APP_PIPEDRIVETOKEN;

        const response = await fetch(getDealDets, options);
        
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        
        return {
            data: data,
            status: response.status
        };

    } catch (error) {
        console.log("Error in getDealDetailsFromPipeDealID:", error);
        return {
            data: null,
            status: 500,
            error: error.message
        };
    }
}

// GET Closed Deal Details
export const getClosedDealDetails = async () => {

    try {
        const headers = new Headers();

        const options = {
            method: "GET",
            headers: headers
        };
        const getDealDets = window.REACT_APP_PIPEDRIVEAPI+"/deals?filter_id=2&api_token="+window.REACT_APP_PIPEDRIVETOKEN;

        const response = await fetch(getDealDets, options);
        const data = await response.json();
        
        return {
            data: data,
            status: response.status
        };

    } catch (error) {
        console.log("Error in getClosedDealDetails:", error);
        return {
            data: null,
            status: 500,
            error: error.message
        };
    }
}

export const checkPerson = async (contact, contactName, phone) => {

    try {
        const headers = new Headers();

        const options = {
            method: "GET",
            headers: headers
        };
        //console.log(contactName);
        const response = await fetch(protectedResources.apiTodoList.pipeSearchPersonEndpoint +  `&term=${contact}`, options);
        const data = await response.json();
        
        return {
            data: data,
            status: response.status
        };

    } catch (error) {
        console.log("Error in checkPerson:", error);
        return {
            data: null,
            status: 500,
            error: error.message
        };
    }

}

// GET DEAL FROM SUBDOMAIN METHOD
export const getDealFromSubdomain = async (id) => {
    try {
        const headers = new Headers();
        const options = {
            method: "GET",
            headers: headers
        };
        const response = await fetch(protectedResources.apiTodoList.pipeGetDealEndpoint +  `&term=${id}`, options);
        const data = await response.json();
        
        return {
            data: data,
            status: response.status
        };
    } catch (error) {
        console.log("Error in getDealFromSubdomain:", error);
        return {
            data: null,
            status: 500,
            error: error.message
        };
    }
}
// GET Salesperson name from Owner ID
export const getSalesGuyFromOwnerID = async (o_id) => {
    try {
        const headers = new Headers();
        const options = {
            method: "GET",
            headers: headers
        };
        const getOwnerDets = window.REACT_APP_PIPEDRIVEAPI+"/users/"+o_id+"?api_token="+window.REACT_APP_PIPEDRIVETOKEN;
        const response = await fetch(getOwnerDets, options);
        const data = await response.json();
        
        return {
            data: data,
            status: response.status
        };
    } catch (error) {
        console.log("Error in getSalesGuyFromOwnerID:", error);
        return {
            data: null,
            status: 500,
            error: error.message
        };
    }
}


export const addPersonToPipe = async (contact, contactName, phone) => {

    try {
        const headers = new Headers();
        headers.append('Content-Type', 'application/json');
        headers.append('Accept', 'application/json');

        const options = {
            method: "POST",
            headers: headers,
            body: JSON.stringify({
                "name": `${contactName}`,
                "email": [
                  {
                    "value": `${contact}`,
                    "primary": "true",
                    "label": "Work"
                  }
                ],
                "phone": [
                  {
                    "value": `${phone}`,
                    "primary": "true",
                    "label": "Work"
                  }
                ]
              })
        };

        const response = await fetch(protectedResources.apiTodoList.pipeAddPersonEndpoint, options);
        const data = await response.json();
        
        return {
            data: data,
            status: response.status
        };

    } catch (error) {
        console.log("Error in addPersonToPipe:", error);
        return {
            data: null,
            status: 500,
            error: error.message
        };
    }

}

export const addPipeLead = async (id, owner_id, pId) => {
    try {
        const accessToken = await getToken();

        const headers = new Headers();
        const bearer = `Bearer ${accessToken}`;
        headers.append('Content-Type', 'application/json');
        headers.append('Accept', 'application/json');

        const options = {
            method: "POST",
            headers: headers,
            body: JSON.stringify({
                "title": `[Signup] ${id}`,
                //"owner_id": parseInt(`${owner_id}`),
                "person_id": parseInt(`${pId}`)
              })
        };

        const response = await fetch(protectedResources.apiTodoList.pipeAddLeadEndpoint, options);
        const data = await response.json();
        
        return {
            data: data,
            status: response.status
        };
    } catch (error) {
        console.log("Error in addPipeLead:", error);
        return {
            data: null,
            status: 500,
            error: error.message
        };
    }
}

//Update LEads
export const updateLeadFields = async (leadId,packageName,clientUrl) => {
    try {
        const accessToken = await getToken();
        clientUrl = clientUrl.toLowerCase();
        const headers = new Headers();
        const bearer = `Bearer ${accessToken}`;
        headers.append('Content-Type', 'application/json');
        headers.append('Accept', 'application/json');

        const pipeleadConfigVar_package = window.REACT_APP_PIPELEAD_PACKAGE;
        const pipeleadConfigVar_subdomain = window.REACT_APP_PIPELEAD_SUBDOMAIN;
        const pipeleadConfigVar_DemoSignup = window.REACT_APP_PIPELEAD_DEMOSIGNUP;
        const pipeleadConfigVar_DemoSignupValue = window.REACT_APP_PIPELEAD_DEMOSIGNUP_VALUE;
        const options = {
            method: "PATCH",
            headers: headers,
            body: JSON.stringify({
                [pipeleadConfigVar_package]: `${packageName}`,
                [pipeleadConfigVar_subdomain]: `${clientUrl}`,
                [pipeleadConfigVar_DemoSignup]: parseInt(`${pipeleadConfigVar_DemoSignupValue}`)
              })
        };

        const addLeadFieldsEP = window.REACT_APP_PIPEDRIVEAPI+"/leads/"+leadId+"?api_token="+window.REACT_APP_PIPEDRIVETOKEN;

        const response = await fetch(addLeadFieldsEP, options);
        const data = await response.json();
        
        return {
            data: data,
            status: response.status
        };

    } catch (error) {
        console.log("Error in updateLeadFields:", error);
        return {
            data: null,
            status: 500,
            error: error.message
        };
    }
}



export const getTask = async (id) => {
    try {
        const accessToken = await getToken();

        const headers = new Headers();
        const bearer = `Bearer ${accessToken}`;

        headers.append("Authorization", bearer);

        const options = {
            method: "GET",
            headers: headers
        };

        const response = await fetch(protectedResources.apiTodoList.todoListEndpoint + '/${id}', options);
        const data = await response.json();
        
        return {
            data: data,
            status: response.status
        };
    } catch (error) {
        console.log("Error in getTask:", error);
        return {
            data: null,
            status: 500,
            error: error.message
        };
    }
}

export const postTask = async (task) => {
    try {
        const accessToken = await getToken();

        const headers = new Headers();
        const bearer = `Bearer ${accessToken}`;

        headers.append("Authorization", bearer);
        headers.append('Content-Type', 'application/json');

        const options = {
            method: "POST",
            headers: headers,
            body: JSON.stringify(task)
        };

        const response = await fetch(protectedResources.apiTodoList.todoListEndpoint, options);
        const data = await response.json();
        
        return {
            data: data,
            status: response.status
        };
    } catch (error) {
        console.log("Error in postTask:", error);
        return {
            data: null,
            status: 500,
            error: error.message
        };
    }
}



export const validateTrial = async(id) => {
    try {
        const accessToken = await getToken();

        const headers = new Headers();
        const bearer = `Bearer ${accessToken}`;

        headers.append("Authorization", bearer);
        headers.append('Content-Type', 'application/json');

        var values = {
           "Account": id
         }


        const options = {
            method: "POST",
            headers: headers,
            body: JSON.stringify(values)
        };

        const response = await fetch(protectedResources.apiTodoList.validateEndpoint, options);
        const data = await response.json();
        
        return {
            data: data,
            status: response.status
        };

    } catch (error) {
        console.log("Error in validateTrial:", error);
        return {
            data: null,
            status: 500,
            error: error.message
        };
    }
}

export const markTrial = async(id) => {
    try {
        const accessToken = await getToken();

        const headers = new Headers();
        const bearer = `Bearer ${accessToken}`;

        headers.append("Authorization", bearer);
        headers.append('Content-Type', 'application/json');

        var values = {
           "Account": id
         }


        const options = {
            method: "POST",
            headers: headers,
            body: JSON.stringify(values)
        };

        const response = await fetch(protectedResources.apiTodoList.markEndpoint, options);
        const data = await response.json();
        
        return {
            data: data,
            status: response.status
        };

    } catch (error) {
        console.log("Error in markTrial:", error);
        return {
            data: null,
            status: 500,
            error: error.message
        };
    }
}

export const extendTrial = async(id,formattedDate) => {
    try {
        const accessToken = await getToken();

        const headers = new Headers();
        const bearer = `Bearer ${accessToken}`;

        headers.append("Authorization", bearer);
        headers.append('Content-Type', 'application/json');

        var values = {
           "Account": id,
           "expiration_date": formattedDate
         }
         
        const options = {
            method: "POST",
            headers: headers,
            body: JSON.stringify(values)
        };

        const response = await fetch(protectedResources.apiTodoList.extendEndpoint, options);
        const data = await response.json();
        
        return {
            data: data,
            status: response.status
        };

    } catch (error) {
        console.log("Error in extendTrial:", error);
        return {
            data: null,
            status: 500,
            error: error.message
        };
    }
}

export const blockTrial = async(id) => {
    try {
        const accessToken = await getToken();

        const headers = new Headers();
        const bearer = `Bearer ${accessToken}`;

        headers.append("Authorization", bearer);
        headers.append('Content-Type', 'application/json');

        var values = {
           "Account": id
         }


        const options = {
            method: "POST",
            headers: headers,
            body: JSON.stringify(values)
        };

        const response = await fetch(protectedResources.apiTodoList.blockEndpoint, options);
        const data = await response.json();
        
        return {
            data: data,
            status: response.status
        };

    } catch (error) {
        console.log("Error in blockTrial:", error);
        return {
            data: null,
            status: 500,
            error: error.message
        };
    }
}

export const endTrial = async(id) => {
    try {
        const accessToken = await getToken();

        const headers = new Headers();
        const bearer = `Bearer ${accessToken}`;

        headers.append("Authorization", bearer);
        headers.append('Content-Type', 'application/json');

        var values = {
           "Account": id
         }


        const options = {
            method: "POST",
            headers: headers,
            body: JSON.stringify(values)
        };

        const response = await fetch(protectedResources.apiTodoList.endTrialEndpoint, options);
        const data = await response.json();
        
        return {
            data: data,
            status: response.status
        };

    } catch (error) {
        console.log("Error in endTrial:", error);
        return {
            data: null,
            status: 500,
            error: error.message
        };
    }
}

export const syncToHubspot = async (payload) => {
    try {
        // Use CORS proxy to call the external API
        const corsProxyUrl = 'https://corsproxy.io/?';
        const targetUrl = 'https://notify.alertops.com/POSTAlert/8e5f236f-2f37-437c-9df8-797640836272/adminsignups';
        
        const response = await fetch(corsProxyUrl + encodeURIComponent(targetUrl), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            },
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.text(); // Response is alphanumeric ID as text
        console.log('Sync response data:', data);
        return {
            data: data,
            status: response.status
        };
    } catch (error) {
        console.log("Error in syncToHubspot:", error);
        return {
            data: null,
            status: 500,
            error: error.message
        };
    }
}

export const getAlertId = async (alphanumericId) => {
    try {
        // Remove any quotes from the alphanumeric ID
        const cleanId = alphanumericId.replace(/"/g, '');
        
        // Use CORS proxy for the AlertOps API
        const corsProxyUrl = 'https://corsproxy.io/?';
        const targetUrl = `https://api.alertops.com/api/v2/integrations/inbound/events/${cleanId}`;
        
        const response = await fetch(corsProxyUrl + encodeURIComponent(targetUrl), {
            method: 'GET',
            headers: {
                'api-key': 'ff9b2941-846e-40cd-8f6f-f21b6c0a9c84',
                'Content-Type': 'application/json',
            }
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log('Alert ID response data:', data);
        return {
            data: data,
            status: response.status
        };
    } catch (error) {
        console.log("Error in getAlertId:", error);
        return {
            data: null,
            status: 500,
            error: error.message
        };
    }
}

// Search HubSpot deals by subdomain (server-proxied)
export const getHubspotDealBySubdomain = async (subdomain) => {
    try {
        const headers = new Headers();
        headers.append('Content-Type', 'application/json');
        headers.append('Hs-api-key', 'pat-na1-12f3d088-deef-450d-b26c-d3c122b83184');

        const url = `http://localhost:5000/api/deals/search/name-get?name=${encodeURIComponent(subdomain)}`;

        const response = await fetchWithRetry(url, { method: 'GET', headers });
        const data = await response.json();

        return {
            data: data,
            status: response.status
        };
    } catch (error) {
        return handleFetchError(error, 'getHubspotDealBySubdomain');
    }
}

export const testCorsSupport = async (url, method = 'GET', headers = {}) => {
    try {
        console.log(`Testing CORS support for: ${url}`);
        
        // Test direct access
        const directResponse = await fetch(url, {
            method: method,
            headers: {
                'Content-Type': 'application/json',
                ...headers
            }
        });
        
        console.log('✅ Direct access successful - CORS supported');
        return {
            corsSupported: true,
            needsProxy: false,
            status: directResponse.status,
            headers: Object.fromEntries(directResponse.headers.entries())
        };
        
    } catch (error) {
        console.log('❌ Direct access failed:', error.message);
        
        // Test with CORS proxy
        try {
            const corsProxyUrl = 'https://corsproxy.io/?';
            const proxyResponse = await fetch(corsProxyUrl + encodeURIComponent(url), {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                    ...headers
                }
            });
            
            console.log('✅ CORS proxy successful');
            return {
                corsSupported: false,
                needsProxy: true,
                status: proxyResponse.status,
                proxyWorks: true
            };
            
        } catch (proxyError) {
            console.log('❌ CORS proxy also failed:', proxyError.message);
            return {
                corsSupported: false,
                needsProxy: true,
                proxyWorks: false,
                error: proxyError.message
            };
        }
    }
};

// Test specific endpoints
export const testSyncEndpoints = async () => {
    console.log('🔍 Testing sync endpoints...');
    
    // Test Hubspot endpoint
    const hubspotTest = await testCorsSupport(
        'https://notify.alertops.com/POSTAlert/8e5f236f-2f37-437c-9df8-797640836272/adminsignups',
        'POST'
    );
    
    // Test AlertOps endpoint
    const alertOpsTest = await testCorsSupport(
        'https://api.alertops.com/api/v2/integrations/inbound/events/test-id',
        'GET',
        { 'api-key': 'ff9b2941-846e-40cd-8f6f-f21b6c0a9c84' }
    );
    
    console.log('📊 Test Results:', {
        hubspot: hubspotTest,
        alertOps: alertOpsTest
    });
    
    return { hubspot: hubspotTest, alertOps: alertOpsTest };
};

export const getLoginAccess = async (clientUrl) => {
    try {
        const token = await getToken();
        const payload = {
            account: clientUrl
        };
        
        //console.log('API Request Payload:', payload);
        //console.log('API Endpoint:', protectedResources.apiTodoList.loginaccessEndpoint);
        
        const response = await fetchWithRetry(
            protectedResources.apiTodoList.loginaccessEndpoint,
            {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload)
            }
        );

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        //console.log('API Response:', data);
        return {
            data: data,
            status: response.status,
            error: null
        };
    } catch (error) {
        return handleFetchError(error, 'getLoginAccess');
    }
};